// skillgenpro-frontend/src/error-framework/recoveryEngine.js
//
// Given a NormalizedError (from errorMapper.js), decides HOW to recover:
// retry now, retry automatically with backoff, or hand off to the user via
// ErrorExperience. Also exports `withAutoRetry`, a small helper any service
// (fetchWrapper, a component, an AI-call site) can wrap an async function
// with to get the "auto-retry transient failures" behavior from Phase 10
// without every call site re-implementing backoff math.
//
// Framework-agnostic (no React) — safe to use from plain services too.

const MAX_AUTO_RETRIES = 3;
const BASE_DELAY_MS = 1000;

/**
 * @param {import('./errorMapper').NormalizedError} normalizedError
 * @returns {{ shouldAutoRetry: boolean, maxAutoRetries: number, nextDelayMs: (attempt: number) => number }}
 */
export function getRecoveryPlan(normalizedError) {
  const shouldAutoRetry = !!(normalizedError && normalizedError.autoRetry && normalizedError.retryable);
  return {
    shouldAutoRetry,
    maxAutoRetries: shouldAutoRetry ? MAX_AUTO_RETRIES : 0,
    // Exponential backoff with a light jitter so many failing clients don't
    // all retry in lockstep against an already-struggling service.
    nextDelayMs: (attempt) => {
      const exp = BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt - 1));
      const jitter = Math.floor(Math.random() * 250);
      return Math.min(exp, 15000) + jitter;
    },
  };
}

/**
 * Wraps an async function with auto-retry behavior driven by a
 * NormalizedError's recovery plan. The wrapped function is called once
 * up-front; if it throws, the thrown value is normalized (via the supplied
 * `normalize` function, kept as a parameter to avoid a circular import with
 * errorMapper.js) and, if the recovery plan says to auto-retry, retried with
 * backoff up to `maxAutoRetries` times before finally re-throwing the last
 * normalized error.
 *
 * @param {() => Promise<any>} fn
 * @param {(err: any) => any} normalize - typically errorMapper's normalizeError
 * @param {(attempt: number, delayMs: number, normalizedError: any) => void} [onRetry] - optional progress callback (e.g. to update a countdown in the UI)
 */
export async function withAutoRetry(fn, normalize, onRetry) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (rawError) {
      const normalized = normalize(rawError);
      const plan = getRecoveryPlan(normalized);
      attempt += 1;
      if (!plan.shouldAutoRetry || attempt > plan.maxAutoRetries) {
        throw normalized;
      }
      const delay = plan.nextDelayMs(attempt);
      if (typeof onRetry === 'function') {
        try {
          onRetry(attempt, delay, normalized);
        } catch (cbErr) {
          // A broken progress callback must not break the retry loop.
        }
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}
